import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Menu, X, User, Wallet, BarChart3, Sparkles, Crown, Landmark,
  LogOut, Shield, FileText, ChevronRight,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * Top-right hamburger drawer. Pure CSS slide-in, no portal library.
 * Items route to existing pages; Connect Bank / Ask Hero pop a custom event
 * that the Dashboard listens for and triggers its own handlers, so we don't
 * duplicate the linking/coach launch logic.
 */
export default function AppMenu() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { logout } = useAuth();

  // ESC closes
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open]);

  const go = (path) => { setOpen(false); navigate(path); };

  const fireEvent = (name) => {
    setOpen(false);
    // small delay so the drawer animates out first
    setTimeout(() => window.dispatchEvent(new CustomEvent(name)), 220);
  };

  const items = [
    { label: "Account",            icon: User,       onClick: () => go("/account"),            testId: "menu-account" },
    { label: "Connect bank",       icon: Landmark,   onClick: () => fireEvent("mmh:connect-bank"), testId: "menu-connect-bank" },
    { label: "Hero Vault",         icon: Wallet,     onClick: () => go("/vault"),              testId: "menu-vault" },
    { label: "Spending habits",    icon: BarChart3,  onClick: () => go("/analytics"),          testId: "menu-analytics" },
    { label: "Ask Hero Coach",     icon: Sparkles,   onClick: () => fireEvent("mmh:open-coach"), testId: "menu-coach" },
    { label: "Plan & billing",     icon: Crown,      onClick: () => go("/upgrade"),            testId: "menu-upgrade" },
    { label: "Security & MFA",     icon: Shield,     onClick: () => go("/account#mfa"),        testId: "menu-security" },
    { label: "Privacy policy",     icon: FileText,   onClick: () => { setOpen(false); window.open("/privacy.html","_blank","noopener"); }, testId: "menu-privacy" },
  ];

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open menu"
        className="h-10 w-10 rounded-2xl bg-slate-900 text-amber-400 hover:bg-slate-800 flex items-center justify-center shadow-md"
        data-testid="menu-launcher"
      >
        <Menu className="w-5 h-5" strokeWidth={2.8} />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="menu-overlay"
        >
          <aside
            onClick={(e) => e.stopPropagation()}
            className="hero-theme absolute top-0 right-0 h-full w-[82%] max-w-sm bg-[#06190F] border-l border-[#1B4238] shadow-2xl flex flex-col"
            data-testid="menu-drawer"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#1B4238]">
              <p className="text-white font-extrabold text-base" style={{ fontFamily: "Outfit, sans-serif" }}>
                My Money Hero
              </p>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="w-9 h-9 rounded-full hover:bg-[#11413B] text-[#C7DAD3] flex items-center justify-center"
                aria-label="Close menu"
                data-testid="menu-close"
              >
                <X className="w-5 h-5" strokeWidth={2.4} />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto py-2">
              {items.map((it) => {
                const Icon = it.icon;
                return (
                  <button
                    key={it.label}
                    type="button"
                    onClick={it.onClick}
                    className="w-full flex items-center gap-3 px-5 py-3.5 text-left text-[#E1EEE8] hover:bg-[#0E2924] active:bg-[#11413B] transition-colors group"
                    data-testid={it.testId}
                  >
                    <span className="w-9 h-9 rounded-xl bg-[#0E2924] group-hover:bg-[#11413B] border border-[#1B4238] flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4.5 h-4.5 text-[#22E07A]" strokeWidth={2.4} />
                    </span>
                    <span className="flex-1 font-semibold text-[15px]" style={{ fontFamily: "Outfit, sans-serif" }}>
                      {it.label}
                    </span>
                    <ChevronRight className="w-4 h-4 text-[#7B948B]" strokeWidth={2.4} />
                  </button>
                );
              })}
            </nav>

            <div className="border-t border-[#1B4238] p-4">
              <button
                type="button"
                onClick={async () => { await logout(); navigate("/login", { replace: true }); }}
                className="w-full h-11 rounded-2xl border border-red-500/40 bg-transparent text-red-300 hover:bg-red-500/10 font-bold uppercase tracking-wider text-xs inline-flex items-center justify-center gap-2"
                style={{ fontFamily: "Outfit, sans-serif" }}
                data-testid="menu-logout"
              >
                <LogOut className="w-4 h-4" strokeWidth={2.6} /> Sign out
              </button>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
